"use client";

import type { LoginState, MedplumClient } from "@medplum/core";
import { useMedplum } from "@medplum/react-hooks";
import { useIntervalEffect } from "@react-hookz/web";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { clearSessionExpiredMarkerAction } from "@/actions/auth/clear-session-expired-marker";
import { setMedplumCookie } from "@/actions/auth/set-medplum-cookie";
import {
  getSessionExpirationSnapshot,
  markSessionChecking,
  markSessionRestored,
  markSessionVerificationFailed,
  notifySessionExpired,
  readSessionCookieMarkers,
} from "@/lib/auth/session-expired";
import { createLogger } from "@/lib/logger";
import {
  CLIENTSIDE_ACTIVE_LOGIN_STORAGE_KEY,
  getSharedMedplumLogin,
} from "@/lib/medplum/clientside";

const SESSION_MARKER_POLL_INTERVAL_MS = 250;
const logger = createLogger("AuthSessionCoordinator");

/**
 * Puts this tab back on its own credentials so a failed adoption is not also a logout.
 */
async function restorePreviousLogin(
  medplum: MedplumClient,
  previousLogin: LoginState | undefined,
): Promise<void> {
  if (!previousLogin) {
    return;
  }

  try {
    await medplum.setActiveLogin(previousLogin);
  } catch (error) {
    logger.warn("Failed to reinstate this tab's previous login", { error });
  }
}

/**
 * Reconciles server and browser authentication across every same-origin tab.
 */
export function AuthSessionCoordinator({ children }: { children: ReactNode }) {
  const medplum = useMedplum();
  const queryClient = useQueryClient();
  const lastExpiredMarkerRef = useRef<string | undefined>(undefined);
  const lastCandidateMarkerRef = useRef<string | undefined>(undefined);
  const isRecoveringRef = useRef(false);
  const tabProfileReferenceRef = useRef(medplum.getActiveLogin()?.profile?.reference);

  const recoverSharedSession = useCallback(async () => {
    if (isRecoveringRef.current) {
      return;
    }

    isRecoveringRef.current = true;
    const previousLogin = medplum.getActiveLogin();
    try {
      const sharedLogin = getSharedMedplumLogin();
      if (!sharedLogin) {
        markSessionVerificationFailed();
        return;
      }

      // Never silently swap this tab onto a different person's session. Medplum's storage
      // listener reloads the page on a profile change; this is defense in depth in app code.
      const expiredProfileReference =
        getSessionExpirationSnapshot().expiredProfileReference ??
        tabProfileReferenceRef.current ??
        previousLogin?.profile?.reference;
      if (expiredProfileReference && sharedLogin.profile?.reference !== expiredProfileReference) {
        logger.warn("Refused a shared login belonging to a different profile");
        markSessionVerificationFailed();
        return;
      }

      markSessionChecking();
      await medplum.setActiveLogin(sharedLogin);
      await medplum.refreshIfExpired(0);
      const profile = await medplum.getProfileAsync();
      const verifiedLogin = medplum.getActiveLogin();
      if (!profile?.id || !verifiedLogin) {
        await restorePreviousLogin(medplum, previousLogin);
        markSessionVerificationFailed();
        return;
      }

      const cookieResult = await setMedplumCookie(verifiedLogin);
      if (!cookieResult.success) {
        throw new Error(cookieResult.message);
      }

      // Cosmetic cleanup of a short-lived marker — the verified login is already installed,
      // so a failed clear must not report a restored session as failed.
      const clearMarkerResult = await clearSessionExpiredMarkerAction();
      if (!clearMarkerResult.success) {
        logger.warn("Failed to clear the expiry marker after restoring the session", {
          error: clearMarkerResult.message,
        });
      }

      await queryClient.invalidateQueries();
      markSessionRestored();
    } catch (error) {
      logger.warn("Failed to restore the shared browser session", { error });
      await restorePreviousLogin(medplum, previousLogin);
      markSessionVerificationFailed();
    } finally {
      isRecoveringRef.current = false;
    }
  }, [medplum, queryClient]);

  const recoverIfSharedLoginChanged = useCallback(() => {
    // Only a tab that has actually observed an expiry needs recovery. Every refresh-token
    // rotation rewrites the candidate cookie, and Medplum's own storage listener already
    // adopts a rotated token for the same profile in place via setAccessToken — so a healthy
    // tab must not flash a banner and refetch its whole cache over routine rotation.
    if (!getSessionExpirationSnapshot().recoveryRequired) {
      return;
    }

    const sharedLogin = getSharedMedplumLogin();
    const currentLogin = medplum.getActiveLogin();
    const hasNewSharedLogin = sharedLogin?.refreshToken !== currentLogin?.refreshToken;
    if (hasNewSharedLogin) {
      void recoverSharedSession();
    }
  }, [medplum, recoverSharedSession]);

  useIntervalEffect(() => {
    const markers = readSessionCookieMarkers();
    if (markers.expired && markers.expired !== lastExpiredMarkerRef.current) {
      lastExpiredMarkerRef.current = markers.expired;
      notifySessionExpired();
    }

    if (markers.candidate && markers.candidate !== lastCandidateMarkerRef.current) {
      lastCandidateMarkerRef.current = markers.candidate;
      recoverIfSharedLoginChanged();
    }
  }, SESSION_MARKER_POLL_INTERVAL_MS);

  useEffect(() => {
    const handleSharedLoginChange = (event: StorageEvent) => {
      if (event.key !== CLIENTSIDE_ACTIVE_LOGIN_STORAGE_KEY) {
        return;
      }
      if (!event.newValue) {
        notifySessionExpired();
        return;
      }
      recoverIfSharedLoginChanged();
    };

    window.addEventListener("storage", handleSharedLoginChange);
    return () => window.removeEventListener("storage", handleSharedLoginChange);
  }, [recoverIfSharedLoginChanged]);

  return children;
}
